import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Globe, LayoutDashboard, LogOut, Plus, Repeat, Search, Settings, ShieldHalf } from 'lucide-react';
import type { Locale } from '@btw/shared';
import { useI18n, LOCALES } from '../i18n';
import { usePatchMe, useLogout, useSwitchAccount } from '../api/hooks';
import { Menu } from '../components/Controls';
import { BrandLink, CompanyMark, Pill } from '../components/kit';
import { useToast } from '../components/Toast';
import { errorMessage } from '../components/Feedback';
import { useSession, useClearSession } from './session';
import { useCompany, companyStatusPill } from './company';
import { NotificationBell } from './Notifications';
import { MAIN_NAV } from './nav';
import { personInitials } from '../lib/people';

const LANG_LABEL: Record<Locale, string> = { nl: 'NL', en: 'EN', zh: '中文' };
/** Each language named in itself, so a reader finds theirs whatever the current language is. */
const LANG_NAME: Record<Locale, string> = { nl: 'Nederlands', en: 'English', zh: '中文' };

/** Sets the language: stored in the user's settings when logged in (§4.2), locally otherwise. */
function useSetLanguage() {
  const { locale } = useI18n();
  const { user, setGuestLocale } = useSession();
  const patch = usePatchMe();
  return (l: Locale) => {
    if (l === locale) return;
    if (user) patch.mutate({ locale: l });
    else setGuestLocale(l);
  };
}

/** Language affects only the current user and is stored in their settings (§4.2); logged out it is local only. */
export function LanguageSwitch() {
  const { t, locale } = useI18n();
  const set = useSetLanguage();
  return (
    <div className="lang-switch" role="group" aria-label={t('lang.label')} translate="no">
      {LOCALES.map((l) => (
        <button key={l} type="button" aria-pressed={l === locale} onClick={() => set(l)} lang={l === 'zh' ? 'zh-CN' : l}>
          {LANG_LABEL[l]}
        </button>
      ))}
    </div>
  );
}

/** Header language button (2026-11-03, J): globe + current language; a menu with the three languages. */
export function LanguageMenu() {
  const { t, locale } = useI18n();
  const set = useSetLanguage();
  return (
    <div className="lang-menu" translate="no">
      <Menu
        label={t('lang.label')}
        width={200}
        trigger={({ toggle, props }) => (
          <button type="button" className="lang-menu__btn" onClick={toggle} {...props} aria-label={t('lang.current', { lang: LANG_NAME[locale] })} title={t('lang.label')}>
            <Globe size={18} strokeWidth={1.75} aria-hidden="true" />
            <span className="lang-menu__code">{LANG_LABEL[locale]}</span>
            <ChevronDown size={14} aria-hidden="true" className="lang-menu__chev" />
          </button>
        )}
      >
        {(close) =>
          LOCALES.map((l) => (
            <button
              key={l}
              role="menuitemradio"
              aria-checked={l === locale}
              type="button"
              className="menu-item"
              lang={l === 'zh' ? 'zh-CN' : l}
              onClick={() => {
                set(l);
                close();
              }}
            >
              <span style={{ flex: 1 }}>{LANG_NAME[l]}</span>
              {l === locale && <Check size={16} aria-hidden="true" />}
            </button>
          ))
        }
      </Menu>
    </div>
  );
}

/** The last address seen in the app and in the back office, so switching returns to where you were. */
const lastPath = { app: '/', ops: '/ops' };

/**
 * Staff only (2026-11-03, J): switch between the customer app ("App") and the back office, as two tabs in the header.
 */
export function ModeSwitch({ mode }: { mode: 'app' | 'ops' }) {
  const { t } = useI18n();
  const { user } = useSession();
  const { pathname, search } = useLocation();
  lastPath[mode] = pathname + search;
  if (!user?.staffRole) return null;
  return (
    <nav className="mode-switch" aria-label={t('mode.label')}>
      <Link to={mode === 'app' ? pathname + search : lastPath.app} className="mode-switch__link" aria-current={mode === 'app' ? 'page' : undefined}>
        <LayoutDashboard size={16} strokeWidth={1.75} aria-hidden="true" />
        <span>{t('mode.app')}</span>
      </Link>
      <Link to={mode === 'ops' ? pathname + search : lastPath.ops} className="mode-switch__link" aria-current={mode === 'ops' ? 'page' : undefined}>
        <ShieldHalf size={16} strokeWidth={1.75} aria-hidden="true" />
        <span>{t('mode.ops')}</span>
      </Link>
    </nav>
  );
}

/** The three main sections (desktop/tablet). Exactly three links — see MAIN_NAV. */
export function MainNav() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  return (
    <nav className="appnav" aria-label={t('nav.main')}>
      {MAIN_NAV.map((item) => (
        <Link key={item.to} to={item.to} className="appnav__link" aria-current={item.match(pathname) ? 'page' : undefined}>
          {t(item.label)}
        </Link>
      ))}
    </nav>
  );
}

/**
 * Company switcher: one company at a time. One company → just its name; several → a menu where every company shows
 * its status pill, with "Bedrijf toevoegen" (onboarding) as the last item.
 */
export function CompanySwitcher() {
  const { t } = useI18n();
  const { companies, current, setCompany } = useCompany();
  if (!current) {
    if (companies.length > 0) return null; // still resolving
    return (
      <Link className="company-switch__btn" to="/onboarding?add=1&step=company">
        <Plus size={16} aria-hidden="true" /> {t('company.add')}
      </Link>
    );
  }
  if (companies.length === 1)
    return (
      <div className="company-switch">
        <span className="company-switch__btn company-switch__btn--static">
          <CompanyMark entity={current.entity} size="sm" />
          <span className="company-switch__name">{current.entity.name}</span>
        </span>
      </div>
    );
  return (
    <div className="company-switch">
      <Menu
        label={t('company.listLabel')}
        width={340}
        trigger={({ toggle, props }) => (
          <button type="button" className="company-switch__btn" onClick={toggle} {...props} aria-label={t('company.switchLabel', { name: current.entity.name })}>
            <CompanyMark entity={current.entity} size="sm" />
            <span className="company-switch__name">{current.entity.name}</span>
            <ChevronDown size={14} aria-hidden="true" />
          </button>
        )}
      >
        {(close) => (
          <>
            {companies.map((c) => {
              const pill = companyStatusPill(c, t);
              const selected = c.entity.id === current.entity.id;
              return (
                <button
                  key={c.entity.id}
                  role="menuitem"
                  type="button"
                  className="menu-item company-switch__item"
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => {
                    setCompany(c.entity.id);
                    close();
                  }}
                >
                  <CompanyMark entity={c.entity} />
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.entity.name}</span>
                    {selected && <Check size={16} aria-label={t('company.current')} style={{ flex: 'none', marginLeft: 'auto' }} />}
                  </span>
                  <Pill tone={pill.tone}>{pill.label}</Pill>
                </button>
              );
            })}
            <div className="menu-sep" />
            <Link role="menuitem" className="menu-item" to="/onboarding?add=1&step=company" onClick={close}>
              <Plus size={18} aria-hidden="true" /> {t('company.add')}
            </Link>
          </>
        )}
      </Menu>
    </div>
  );
}

/** Avatar menu: settings, back office or app (staff only), switch account (if any), log out. Language is in the header. */
export function AccountMenu({ mode = 'app' }: { mode?: 'app' | 'ops' }) {
  const { t } = useI18n();
  const { user, account, me } = useSession();
  const logout = useLogout();
  const switchAccount = useSwitchAccount();
  const clear = useClearSession();
  const navigate = useNavigate();
  const toast = useToast();
  if (!user) return null;
  const doLogout = async () => {
    try {
      await logout.mutateAsync();
    } finally {
      clear();
      navigate('/login', { replace: true });
    }
  };
  return (
    <div className="account-menu">
      <Menu
        label={t('account.menu')}
        width={280}
        trigger={({ toggle, props }) => (
          <button type="button" className="account-btn" onClick={toggle} {...props} aria-label={t('account.menuFor', { name: user.name })}>
            <span className="user-avatar" aria-hidden="true">
              {personInitials(user.name)}
            </span>
          </button>
        )}
      >
        {(close) => (
          <>
            <div style={{ padding: '8px 10px 10px' }}>
              <strong style={{ display: 'block' }}>{user.name}</strong>
              <span className="muted small">{account?.name ? `${account.name} · ` : ''}{user.email}</span>
            </div>
            <div className="menu-sep" />
            <Link role="menuitem" className="menu-item" to="/instellingen" onClick={close}>
              <Settings size={18} aria-hidden="true" /> {t('nav.settings')}
            </Link>
            {user.staffRole &&
              (mode === 'ops' ? (
                <Link role="menuitem" className="menu-item" to={lastPath.app} onClick={close}>
                  <LayoutDashboard size={18} aria-hidden="true" /> {t('mode.toApp')}
                </Link>
              ) : (
                <Link role="menuitem" className="menu-item" to={lastPath.ops} onClick={close}>
                  <ShieldHalf size={18} aria-hidden="true" /> {t('nav.ops')}
                </Link>
              ))}
            {me && me.accounts.length > 1 && (
              <>
                <div className="menu-sep" />
                <div className="menu-label">
                  <Repeat size={12} aria-hidden="true" style={{ display: 'inline', marginRight: 4 }} />
                  {t('account.switch')}
                </div>
                {me.accounts.map((a) => (
                  <button
                    key={a.id}
                    role="menuitem"
                    type="button"
                    className="menu-item"
                    aria-current={a.id === account?.id ? 'true' : undefined}
                    onClick={async () => {
                      close();
                      if (a.id === account?.id) return;
                      try {
                        await switchAccount.mutateAsync(a.id);
                        navigate('/');
                      } catch (e) {
                        toast.show({ message: errorMessage(e, t), tone: 'error' });
                      }
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      {a.name}
                      <span className="muted small" style={{ display: 'block' }}>
                        {t(`role.${a.role}`)}
                      </span>
                    </span>
                    {a.id === account?.id && <Check size={16} aria-hidden="true" />}
                  </button>
                ))}
              </>
            )}
            <div className="menu-sep" />
            <button role="menuitem" type="button" className="menu-item" onClick={doLogout}>
              <LogOut size={18} aria-hidden="true" /> {t('account.logout')}
            </button>
          </>
        )}
      </Menu>
    </div>
  );
}

/**
 * Header: logo · (staff) App / Back office · search · company · language · bell · avatar. In the back office
 * (2026-11-03) the customer-side controls (search, company, bell) are left out.
 */
export function AppHeader({ onOpenSearch, mode = 'app' }: { onOpenSearch: () => void; mode?: 'app' | 'ops' }) {
  const { t } = useI18n();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const app = mode === 'app';
  return (
    <header className={`appbar${app ? '' : ' appbar--ops'}`}>
      <div className="appbar__inner">
        <BrandLink to={app ? '/' : '/ops'} label={app ? t('nav.home') : t('nav.ops')} />
        {app && <MainNav />}
        <ModeSwitch mode={mode} />
        <div className="appbar__right">
          {app && (
            <button type="button" className="icon-btn appbar__search" onClick={onOpenSearch} aria-label={t('nav.search')} aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'} title={`${t('nav.search')} (${isMac ? '⌘' : 'Ctrl'} K)`}>
              <Search size={20} strokeWidth={1.75} aria-hidden="true" />
            </button>
          )}
          {app && <CompanySwitcher />}
          <LanguageMenu />
          {app && <NotificationBell />}
          <AccountMenu mode={mode} />
        </div>
      </div>
    </header>
  );
}
